import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TooltipDirective } from 'sharedFeatures/directives';
import { TooltipType } from 'sharedFeatures/directives/enums/tooltip-type';
import { Utils } from "sharedFeatures/utils";
import { DomSmallUtils } from 'sharedGuiSmall/utils/dom-utils';

declare var jQuery: any;

@Injectable({ providedIn: 'root' })
export class TooltipManagerService {

    constructor(private sanitizer: DomSanitizer) {
    }

    private tooltipTemplate: string = `<div class="brixx-tooltip g2Background p2Font g5Color" data-testid="brixxTooltip">
                                        
                                      </div>`;

    private gantTooltipTemplate: string = `<div class="brixx-gant-tooltip g2Background p2Font g5Color" data-testid="brixxGantTooltip">
                                            
                                          </div>`;

    private componentRowTemplate: string = `<div class="brixx-tooltip g2Background h4Font g5Color" data-testid="TreeViewItemBrixxTooltip">
                                            <div class="tooltip-title"></div>
                                            <div style="height: 5px"></div>
                                            <div class="brixx-component-row-tooltip p2Font g5Color">
                                            
                                            </div>
                                            </div>`;

    private componentRowTemplateWithoutComment: string = `<div class="brixx-tooltip g2Background h4Font g5Color" data-testid="TreeViewItemBrixxTooltip">
                                            <div class="tooltip-title"></div>
                                            <div class="brixx-component-row-tooltip p2Font g5Color">
                                            
                                            </div>
                                            </div>`;

    private actualsTemplate = `<div class="brixx-tooltip g2Background p2Font g5Color" data-testid="actualsBrixxTooltip">
                                        <span class="header-actual-tooltip">Forecast: </span><span id="forecast_value" data-testid="forecastValue"></span>
                                        <br />
                                        <span class="header-actual-tooltip">Actual: </span><span id="actual_value" data-testid="actualValue"></span>
                                      </div>`;

    private tooltipEl: Element | undefined;

    private delayTimer: number = 0;

    private hideTimer: number = 0;

    private tooltipRect: any;


    private activeTooltip: TooltipDirective | undefined;

    private wasShown: boolean = false;

    private sizeCash: Map<string, any> = new Map<string, any>();

    public setActive(tooltip?: TooltipDirective) {

        if (this.activeTooltip) this.reset();

        this.activeTooltip = tooltip;

    }

    public getActive(): TooltipDirective | undefined {

        return this.activeTooltip;

    }


    public setWasShown(wasShown: boolean): void {

        this.wasShown = wasShown;

    }

    public isWasShown(): boolean {

        return this.wasShown;

    }

    public getTooltipSize(text: string, title?: string): any {

        if (this.sizeCash.has(text)) return this.sizeCash.get(text);

        let tooltip = jQuery(this.tooltipTemplate).appendTo(document.body)[0];
        jQuery(tooltip).css('visibility', 'hidden');

        if (!title) jQuery(tooltip).text(text);
        else {
            jQuery(tooltip).find('.tooltip-title').text(title);
            jQuery(tooltip).find('.brixx-component-row-tooltip').text(text);
        }

        let rect = tooltip.getBoundingClientRect();
        jQuery(tooltip).remove();

        this.tooltipRect = rect;

        if (this.sizeCash.size + 1 > 20) this.sizeCash.delete(this.sizeCash.keys().next().value);

        this.sizeCash.set(text, rect);

        return rect;

    }

    private constrainCoordinates(x: number, y: number): { x: number, y: number } {

        let xCalc = x;
        let yCalc = y;

        if (!this.tooltipRect) return { x: xCalc, y: yCalc };

        let width = DomSmallUtils.width(document.body);
        let height = DomSmallUtils.height(document.body);

        if (x + this.tooltipRect.width > width) xCalc = width - this.tooltipRect.width;

        if (y + this.tooltipRect.height > height) yCalc = height - this.tooltipRect.height;

        return { x: xCalc, y: yCalc };

    }

    private cutText(originalTxt: string): { txt: string, wasCut: boolean } {

        let result: { txt: string, wasCut: boolean } = { txt: originalTxt, wasCut: false};

        if (!originalTxt)

            return result;


        let words: Array<string> = originalTxt.split(/\s+|[,.]/);
        words = words.filter((vl) => { if (Utils.isEmpty(vl)) return false; else return true; });


        if (words.length > 75) {
            result.txt = originalTxt.substring(originalTxt.indexOf(words[0]), originalTxt.lastIndexOf(words[74]) + words[74].length);
            result.wasCut = true;
        }


        return result;

    }

    public showAt(text: string, x: number, y: number, title?: string, type?: string, hideAfter?: boolean): void {
        clearTimeout(this.delayTimer);
        if (this.hideTimer) clearTimeout(this.hideTimer);

        const isComponentRowType = type === TooltipType.COMPONENT_ROW;
        const isActuals = type === TooltipType.ACTUALS;

        let template: string;

        if (type === TooltipType.GANT) {
            template = this.gantTooltipTemplate;
        } else if (isComponentRowType) {
            template = this.componentRowTemplate;
        } else if (isActuals) {
            template = this.actualsTemplate;
        } else {
            template = this.tooltipTemplate;
        }

        if (isComponentRowType) {
            const {txt, wasCut} = this.cutText(text);
            if (wasCut) {
                text = `${txt} ...`;
            }
            
            text = this.sanitizer.sanitize(SecurityContext.HTML, text) ?? '';
           
            if (title) {
                title = this.sanitizer.sanitize(SecurityContext.HTML, title) ?? '';
            }
        }
        
        this.delayTimer = window.setTimeout(() => {
            if (!this.tooltipEl) {
                if (isComponentRowType && text === '') {
                    this.tooltipEl = jQuery(this.componentRowTemplateWithoutComment).appendTo(document.body)[0];
                } else {
                    this.tooltipEl = jQuery(template).appendTo(document.body)[0];
                }
            }

            jQuery(this.tooltipEl).css('z-index', jQuery.topZIndex(Utils.zIndexSelector) + 1);

            if (isComponentRowType) {
                jQuery(this.tooltipEl).find('.tooltip-title').html(title);
                jQuery(this.tooltipEl).find('.brixx-component-row-tooltip').html(text);
            } else if (isActuals) {
                jQuery(this.tooltipEl).find('#forecast_value').text(text);
                jQuery(this.tooltipEl).find('#actual_value').text(title);
            } else {
                jQuery(this.tooltipEl).text(text);
            }

            jQuery(this.tooltipEl).show();
            const calcCs = this.constrainCoordinates(x, y);
            jQuery(this.tooltipEl).offset({left: calcCs.x, top: calcCs.y});
        }, 500);

        if (hideAfter !== false) {
            this.hideTimer = window.setTimeout(() => {
                this.hide();
            }, 8000);
        }
    }
    
    public setText(text: string): void {

        if (!text) return;

        if (this.tooltipEl)
            if (!jQuery(this.tooltipEl).find('.tooltip-title').length) jQuery(this.tooltipEl).text(text);
            else {
                jQuery(this.tooltipEl).find('.brixx-component-row-tooltip').text(text);
            }

    }

    public moveAt(x: number, y: number): void {

        if (this.tooltipEl) {
            jQuery(this.tooltipEl).offset({ left: x, top: y });
            jQuery(this.tooltipEl).css('z-index', jQuery.topZIndex(Utils.zIndexSelector) + 1);
        }

    }

    public showForPoint(text: string, x: number, y: number, title?: string, type?: string, hideAfter?: boolean): void {

        this.showAt(text, x, y, title, type, hideAfter);

    }

    public hide(): void {

        window.clearTimeout(this.delayTimer);
        if (this.hideTimer) window.clearTimeout(this.hideTimer);
        if (this.tooltipEl) jQuery(this.tooltipEl).hide();

    }

    public reset(): void {

        if (this.activeTooltip) this.activeTooltip.deactivate();
        this.hide();
        this.setWasShown(false);
        if (this.tooltipEl) jQuery(this.tooltipEl).remove();
        this.tooltipEl = undefined;

    }

}
